import { useState, useEffect, useCallback, useRef } from 'react'
import { tableAssignmentStorage } from '../services/TableAssignmentStorage'
import { TableAssignmentMapper } from '../utils/TableAssignmentMapper'
import { v4 as uuidv4 } from 'uuid'

/**
 * Custom hook for managing table assignments with persistence
 * Handles state management, localStorage sync, and data transformations
 */
export const useTableAssignments = (documentMRTTables, sourceDocument) => {
  const [tableAssignments, setTableAssignments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const documentIdRef = useRef(null)
  const isMountedRef = useRef(true)

  // Get document ID from sourceDocument
  const documentId = sourceDocument?.document_id || null

  /**
   * Initialize assignments from MRT tables and stored data
   */
  useEffect(() => {
    setIsLoading(true)

    // Try to load stored assignments first
    if (documentId) {
      const storedAssignments = tableAssignmentStorage.load(documentId)
      
      if (storedAssignments !== null) {
        // Use stored assignments exactly as they were saved (preserves user modifications)
        // This includes empty arrays if user removed all rows
        const sortedAssignments = TableAssignmentMapper.sortBySectionNumber(storedAssignments)
        setTableAssignments(sortedAssignments)
        console.log(`Restored exact state: ${storedAssignments.length} assignments for document ${documentId}`)
        documentIdRef.current = documentId
        setIsLoading(false)
        return // Exit early - don't reinitialize from MRT tables
      }
    }

    // Only initialize from MRT tables if no stored data exists
    if (!documentMRTTables || documentMRTTables.length === 0) {
      setTableAssignments([]) // Initialize with empty array
      documentIdRef.current = documentId
      setIsLoading(false)
      return
    }

    // Load template tables from storage or use provided ones
    let templateTables = documentMRTTables
    if (documentId) {
      const storedTemplateTables = tableAssignmentStorage.loadTemplateTables(documentId)
      if (storedTemplateTables && storedTemplateTables.length > 0) {
        templateTables = storedTemplateTables
      } else if (documentMRTTables && documentMRTTables.length > 0) {
        // Save template tables for consistency
        tableAssignmentStorage.saveTemplateTables(documentId, documentMRTTables)
      }
    }
    
    // Initialize from MRT tables (first time only)
    const initialAssignments = TableAssignmentMapper.initializeFromMRTTables(
      templateTables,
      sourceDocument
    )

    const sortedAssignments = TableAssignmentMapper.sortBySectionNumber(initialAssignments)
    setTableAssignments(sortedAssignments)

    documentIdRef.current = documentId
    setIsLoading(false)
  }, [documentMRTTables, sourceDocument, documentId])

  /**
   * Save assignments to localStorage
   */
  const saveAssignments = useCallback(() => {
    if (!documentId || !isMountedRef.current) return false

    const assignmentsToStore = TableAssignmentMapper.prepareForStorage(tableAssignments)
    const success = tableAssignmentStorage.save(documentId, assignmentsToStore)
    
    if (success) {
      setHasUnsavedChanges(false)
      console.log(`useTableAssignments: Saved ${assignmentsToStore.length} assignments to localStorage`)
    }
    
    return success
  }, [documentId, tableAssignments])

  /**
   * Clear stored assignments
   */
  const clearStoredAssignments = useCallback(() => {
    if (!documentId) return false
    
    const success = tableAssignmentStorage.clear(documentId)
    if (success) {
      setHasUnsavedChanges(false)
    }
    
    return success
  }, [documentId])

  /**
   * Update a single field in an assignment
   */
  const updateAssignment = useCallback((rowId, field, value) => {
    setTableAssignments(prev => {
      let updated = prev.map(row => {
        if (row.id === rowId) {
          // Special handling for template table updates
          if (field === 'templateTable') {
            // Note: The modal will handle finding the table from templateTableOptions
            // Here we just update the value
            return {
              ...row,
              templateTable: value,
              // The modal should also pass templateTableId if needed
            }
          }
          // Special handling for replacement table updates
          else if (field === 'replacementTable') {
            // This will be handled by the modal with replacement table data
            return {
              ...row,
              [field]: value
            }
          }
          // Standard field update
          else {
            return {
              ...row,
              [field]: value
            }
          }
        }
        return row
      })

      // Sort if section was updated
      if (field === 'section') {
        updated = TableAssignmentMapper.sortBySectionNumber(updated)
      }

      setHasUnsavedChanges(true)
      return updated
    })
  }, [])

  /**
   * Update replacement table with full data
   */
  const updateReplacementTable = useCallback((rowId, value, replacementTableData) => {
    setTableAssignments(prev => {
      const updated = prev.map(row => {
        if (row.id === rowId) {
          return {
            ...row,
            replacementTable: value,
            replacementTableId: replacementTableData?.id || ''
          }
        }
        return row
      })
      
      setHasUnsavedChanges(true)
      return updated
    })
  }, [])

  /**
   * Add a new assignment
   */
  const addAssignment = useCallback(() => {
    const newAssignment = {
      id: uuidv4(),
      section: '',
      templateTable: '',
      templateTableId: '',
      replacementTable: '',
      replacementTableId: '',
      instructions: '',
      isUserAdded: true // Mark as user-added
    }
    
    setTableAssignments(prev => {
      const updated = [...prev, newAssignment]
      const sorted = TableAssignmentMapper.sortBySectionNumber(updated)
      setHasUnsavedChanges(true)
      
      // Immediately save to localStorage when user adds a row
      if (documentId) {
        const assignmentsToStore = TableAssignmentMapper.prepareForStorage(sorted)
        tableAssignmentStorage.save(documentId, assignmentsToStore)
        console.log('useTableAssignments: Saved user-added row immediately')
      }
      
      return sorted
    })
    
    return newAssignment.id
  }, [documentId])

  /**
   * Remove an assignment
   */
  const removeAssignment = useCallback((rowId) => {
    setTableAssignments(prev => {
      const updated = prev.filter(row => row.id !== rowId)
      setHasUnsavedChanges(true)
      
      // Immediately save to localStorage when user removes a row
      if (documentId) {
        const assignmentsToStore = TableAssignmentMapper.prepareForStorage(updated)
        tableAssignmentStorage.save(documentId, assignmentsToStore)
        console.log('useTableAssignments: Saved after removing row')
      }
      
      return updated
    })
  }, [documentId])

  /**
   * Remove all assignments
   */
  const removeAllAssignments = useCallback(() => {
    setTableAssignments([])
    setHasUnsavedChanges(true)
  }, [])

  /**
   * Get prepared assignments for Insert All
   */
  const getPreparedAssignments = useCallback(() => {
    return TableAssignmentMapper.prepareForInsertAll(tableAssignments)
  }, [tableAssignments])

  /**
   * Get assignment statistics
   */
  const getStatistics = useCallback(() => {
    return TableAssignmentMapper.getStatistics(tableAssignments)
  }, [tableAssignments])

  /**
   * Handle cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return {
    // State
    tableAssignments,
    isLoading,
    hasUnsavedChanges,
    documentId,
    
    // Actions
    updateAssignment,
    updateReplacementTable,
    addAssignment,
    removeAssignment,
    removeAllAssignments,
    saveAssignments,
    clearStoredAssignments,
    
    // Utilities
    getPreparedAssignments,
    getStatistics
  }
}